import { useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore.js';
import { useToastStore } from '../../store/useToastStore.js';
import { incrementGoal } from '../../api/client.js';
import { computeGoalProgress, getPeriodStart } from '../../utils/goalProgress.js';
import { GoalMetric, GoalPeriod } from '@launchpad/shared';

export function GoalsCard() {
  const applications = useAppStore((s) => s.applications);
  const goals = useAppStore((s) => s.goals);
  const updateGoalLocal = useAppStore((s) => s.updateGoalLocal);
  const addToast = useToastStore((s) => s.addToast);
  const navigate = useNavigate();
  const prevProgress = useRef(null);

  const progress = useMemo(() => computeGoalProgress(applications, goals), [applications, goals]);

  // Celebration toast when goal crosses target
  useEffect(() => {
    if (!prevProgress.current) { prevProgress.current = progress; return; }
    for (const p of progress) {
      const prev = prevProgress.current.find((x) => x.goalId === p.goalId);
      if (p.complete && prev && !prev.complete) {
        const goal = goals.find((g) => g.goalId === p.goalId);
        addToast(`🎉 Goal complete: ${goal?.title}!`, 'success');
      }
    }
    prevProgress.current = progress;
  }, [progress]);

  if (goals.length === 0) return null;

  async function handleIncrement(goalId) {
    updateGoalLocal(goalId, { current: (goals.find((g) => g.goalId === goalId)?.current || 0) + 1 });
    try { await incrementGoal(goalId); }
    catch { updateGoalLocal(goalId, { current: (goals.find((g) => g.goalId === goalId)?.current || 0) - 1 }); }
  }

  function handleNavigate(goal) {
    const periodStart = getPeriodStart(goal.period, goal.createdAt);
    const dateFrom = periodStart.slice(0, 10);
    const boardType = goal.metric === GoalMetric.NETWORKING_OUTREACH || goal.metric === GoalMetric.CONVERSATIONS_COMPLETED ? 'networking' : 'application';
    navigate(`/board?boardType=${boardType}&dateFrom=${dateFrom}`);
  }

  return (
    <div className="rounded border border-slate-200 p-4 space-y-3">
      <h2 className="text-sm font-semibold text-slate-900">🎯 Goals</h2>
      {progress.map((p) => {
        const goal = goals.find((g) => g.goalId === p.goalId);
        if (!goal) return null;
        const pct = Math.min(100, Math.round((p.current / p.target) * 100));

        return (
          <div key={p.goalId} className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <button onClick={() => handleNavigate(goal)} className="text-xs text-slate-700 font-medium truncate hover:text-primary-600 text-left">
                {goal.title}
              </button>
              <div className="flex items-center gap-2 shrink-0">
                {p.streak > 0 && <span className="text-[10px] text-amber-600">🔥 {p.streak}</span>}
                <span className={`text-xs font-semibold ${p.complete ? 'text-green-600' : 'text-slate-600'}`}>
                  {p.complete && '✓ '}{p.current}/{p.target}
                </span>
                {goal.metric === GoalMetric.CUSTOM && (
                  <button onClick={() => handleIncrement(goal.goalId)} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-200">+1</button>
                )}
              </div>
            </div>
            <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
              <div className={`h-full rounded ${p.complete ? 'bg-green-500' : 'bg-primary-500'}`} style={{ width: `${pct}%` }} />
            </div>
            {p.lastPeriod && (
              <p className="text-[10px] text-slate-400">
                Last {goal.period === GoalPeriod.WEEKLY ? 'week' : 'month'}: {p.lastPeriod.current}/{p.lastPeriod.target} {p.lastPeriod.complete ? '✓' : '✗'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
